import { tokens } from '@/design';

export type PolygonSymbolFamily = '2d' | '3d-material';
export type PolygonFillMode =
  | 'solid'
  | 'none'
  | 'hatch'
  | 'crosshatch'
  | 'dots'
  | 'grid'
  | 'material';
export type PolygonMaterialKind =
  | 'asphalt'
  | 'brick'
  | 'concrete'
  | 'forest'
  | 'grass'
  | 'water'
  | 'mulch'
  | 'sand';

export interface PolygonSymbolState {
  readonly presetId: string;
  readonly family: PolygonSymbolFamily;
  readonly fillMode: PolygonFillMode;
  readonly foregroundColor: string;
  readonly backgroundColor: string;
  readonly scale: number;
  readonly rotation: number;
  readonly material?: PolygonMaterialKind;
}

export interface PolygonSymbolPreset extends PolygonSymbolState {
  readonly id: string;
  readonly label: string;
  readonly description: string;
}

export const DEFAULT_POLYGON_SYMBOL: PolygonSymbolState = {
  presetId: 'solid',
  family: '2d',
  fillMode: 'solid',
  foregroundColor: tokens.color.accent.subtle,
  backgroundColor: tokens.color.accent.subtle,
  scale: 1,
  rotation: 0,
};

const twoD: ReadonlyArray<PolygonSymbolPreset> = [
  preset('solid', 'Solid fill', 'Simple filled polygon.', '2d', 'solid', tokens.color.accent.subtle, tokens.color.accent.subtle),
  preset('outline', 'Outline only', 'Transparent area with an outline.', '2d', 'none', tokens.color.ink.primary, tokens.color.surface[0]),
  preset('diagonal', 'Diagonal hatch', 'Single diagonal line fill.', '2d', 'hatch', tokens.color.ink.secondary, tokens.color.surface[1], 1, 45),
  preset('horizontal', 'Horizontal hatch', 'Horizontal line fill.', '2d', 'hatch', tokens.color.ink.secondary, tokens.color.surface[1], 1, 0),
  preset('crosshatch', 'Crosshatch', 'Crossed diagonal line fill.', '2d', 'crosshatch', tokens.color.ink.secondary, tokens.color.surface[1], 1, 45),
  preset('dots', 'Dotted', 'Regular dot pattern.', '2d', 'dots', tokens.color.semantic.warning, tokens.color.surface[1]),
  preset('dense-dots', 'Dense dots', 'Dense dot pattern.', '2d', 'dots', tokens.color.semantic.info, tokens.color.surface[1], 0.65),
  preset('grid', 'Grid', 'Square grid fill.', '2d', 'grid', tokens.color.accent.primary, tokens.color.surface[1]),
];

const materials: ReadonlyArray<PolygonSymbolPreset> = [
  material('asphalt', 'Asphalt', tokens.color.ink.muted, tokens.color.surface[2]),
  material('brick', 'Brick', tokens.color.semantic.danger, tokens.color.surface[2]),
  material('concrete', 'Concrete', tokens.color.ink.secondary, tokens.color.surface[2]),
  material('forest', 'Forest', tokens.color.accent.strong, tokens.color.surface[1]),
  material('grass', 'Grass', tokens.color.semantic.success, tokens.color.accent.strong),
  material('water', 'Water', tokens.color.semantic.info, tokens.color.surface[1]),
  material('mulch', 'Mulch', tokens.color.semantic.warning, tokens.color.surface[2]),
  material('sand', 'Sand', tokens.color.semantic.warning, tokens.color.surface[1]),
];

export const POLYGON_SYMBOL_PRESETS: ReadonlyArray<PolygonSymbolPreset> = [
  ...twoD,
  ...materials,
];

export function polygonSymbolPresets(
  family: PolygonSymbolFamily,
): ReadonlyArray<PolygonSymbolPreset> {
  return POLYGON_SYMBOL_PRESETS.filter((entry) => entry.family === family);
}

export function resolvePolygonSymbolPreset(id: string): PolygonSymbolPreset {
  return POLYGON_SYMBOL_PRESETS.find((entry) => entry.id === id)
    ?? POLYGON_SYMBOL_PRESETS[0];
}

export function applyPolygonSymbolPreset(
  current: PolygonSymbolState,
  id: string,
): PolygonSymbolState {
  const selected = resolvePolygonSymbolPreset(id);
  return {
    ...current,
    presetId: selected.id,
    family: selected.family,
    fillMode: selected.fillMode,
    foregroundColor: selected.foregroundColor,
    backgroundColor: selected.backgroundColor,
    scale: selected.scale,
    rotation: selected.rotation,
    material: selected.material,
  };
}

function preset(
  id: string,
  label: string,
  description: string,
  family: PolygonSymbolFamily,
  fillMode: PolygonFillMode,
  foregroundColor: string,
  backgroundColor: string,
  scale = 1,
  rotation = 0,
): PolygonSymbolPreset {
  return {
    id,
    label,
    description,
    presetId: id,
    family,
    fillMode,
    foregroundColor,
    backgroundColor,
    scale,
    rotation,
  };
}

function material(
  materialKind: PolygonMaterialKind,
  label: string,
  foregroundColor: string,
  backgroundColor: string,
): PolygonSymbolPreset {
  return {
    ...preset(
      `material-${materialKind}`,
      label,
      `${label} flat material texture.`,
      '3d-material',
      'material',
      foregroundColor,
      backgroundColor,
    ),
    material: materialKind,
  };
}

const BASE_TILE_SIZE = tokens.space[6];

export function polygonPatternKey(symbol: PolygonSymbolState): string {
  return [
    symbol.presetId,
    symbol.fillMode,
    symbol.material ?? '',
    symbol.foregroundColor,
    symbol.backgroundColor,
    symbol.scale,
    symbol.rotation,
  ].join(':');
}

export function polygonPatternSvg(
  symbol: PolygonSymbolState,
  size: number = BASE_TILE_SIZE,
): string {
  const tile = Math.max(tokens.space[2], Math.round(size * symbol.scale));
  const background = symbol.fillMode === 'none' ? 'none' : symbol.backgroundColor;
  const pattern = patternMarkup(symbol, tile);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" fill="${background}"/><g transform="rotate(${symbol.rotation} ${size / 2} ${size / 2})">${pattern}</g></svg>`;
}

export function polygonPatternSvgDataUrl(
  symbol: PolygonSymbolState,
  size: number = BASE_TILE_SIZE,
): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(polygonPatternSvg(symbol, size))}`;
}

export function createPolygonPatternCanvas(
  symbol: PolygonSymbolState,
  opacity = 1,
): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const size = Math.max(tokens.space[2], Math.round(BASE_TILE_SIZE * symbol.scale));
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.globalAlpha = Math.max(0, Math.min(1, opacity));
  drawProceduralPattern(context, symbol, size);
  return canvas;
}

function patternMarkup(symbol: PolygonSymbolState, size: number): string {
  const color = symbol.foregroundColor;
  const thin = Math.max(1, tokens.border.width.thin);
  switch (symbol.fillMode) {
    case 'none':
    case 'solid':
      return '';
    case 'hatch':
      return `<path d="M0 ${size / 2}H${size}" stroke="${color}" stroke-width="${thin}"/>`;
    case 'crosshatch':
      return `<path d="M0 ${size / 3}H${size}M${size / 3} 0V${size}" stroke="${color}" stroke-width="${thin}"/>`;
    case 'dots':
      return `<circle cx="${size / 4}" cy="${size / 4}" r="${Math.max(1, size / 12)}" fill="${color}"/><circle cx="${size * 0.75}" cy="${size * 0.75}" r="${Math.max(1, size / 12)}" fill="${color}"/>`;
    case 'grid':
      return `<path d="M0 ${size / 2}H${size}M${size / 2} 0V${size}" stroke="${color}" stroke-width="${thin}"/>`;
    case 'material':
      return materialMarkup(symbol, size);
  }
}

function materialMarkup(symbol: PolygonSymbolState, size: number): string {
  const color = symbol.foregroundColor;
  switch (symbol.material) {
    case 'brick':
      return `<path d="M0 ${size / 2}H${size}M${size / 2} 0V${size / 2}M${size / 4} ${size / 2}V${size}" stroke="${color}" stroke-width="2"/>`;
    case 'water':
      return `<path d="M0 ${size / 3}Q${size / 4} 0 ${size / 2} ${size / 3}T${size} ${size / 3}M0 ${size * 0.75}Q${size / 4} ${size / 2} ${size / 2} ${size * 0.75}T${size} ${size * 0.75}" fill="none" stroke="${color}" stroke-width="2"/>`;
    case 'grass':
    case 'forest':
      return `<path d="M${size / 4} ${size}l${size / 8}-${size / 2}m0 ${size / 2}l${size / 8}-${size * 0.65}m${size / 4} ${size * 0.65}l${size / 8}-${size / 2}" stroke="${color}" stroke-width="2"/>`;
    case 'asphalt':
    case 'concrete':
    case 'mulch':
    case 'sand':
    default:
      return `<circle cx="${size / 5}" cy="${size / 4}" r="2" fill="${color}"/><circle cx="${size * 0.65}" cy="${size / 3}" r="1.5" fill="${color}"/><circle cx="${size * 0.45}" cy="${size * 0.8}" r="2" fill="${color}"/>`;
  }
}

function drawProceduralPattern(
  context: CanvasRenderingContext2D,
  symbol: PolygonSymbolState,
  size: number,
): void {
  if (symbol.fillMode !== 'none') {
    context.fillStyle = symbol.backgroundColor;
    context.fillRect(0, 0, size, size);
  }
  context.strokeStyle = symbol.foregroundColor;
  context.fillStyle = symbol.foregroundColor;
  context.lineWidth = Math.max(1, tokens.border.width.thin);
  context.translate(size / 2, size / 2);
  context.rotate((symbol.rotation * Math.PI) / 180);
  context.translate(-size / 2, -size / 2);
  if (symbol.fillMode === 'hatch' || symbol.fillMode === 'crosshatch' || symbol.fillMode === 'grid') {
    context.beginPath();
    context.moveTo(0, size / 2);
    context.lineTo(size, size / 2);
    if (symbol.fillMode !== 'hatch') {
      context.moveTo(size / 2, 0);
      context.lineTo(size / 2, size);
    }
    context.stroke();
  } else if (symbol.fillMode === 'dots') {
    for (const [x, y, radius] of [[0.2, 0.25, 2], [0.65, 0.35, 1.5], [0.45, 0.8, 2]] as const) {
      context.beginPath();
      context.arc(size * x, size * y, radius, 0, Math.PI * 2);
      context.fill();
    }
  } else if (symbol.fillMode === 'material') {
    if (symbol.material === 'brick') {
      context.lineWidth = 2;
      context.beginPath();
      context.moveTo(0, size / 2);
      context.lineTo(size, size / 2);
      context.moveTo(size / 2, 0);
      context.lineTo(size / 2, size / 2);
      context.moveTo(size / 4, size / 2);
      context.lineTo(size / 4, size);
      context.stroke();
    } else if (symbol.material === 'water') {
      context.lineWidth = 2;
      for (const y of [size / 3, size * 0.75]) {
        context.beginPath();
        context.moveTo(0, y);
        context.quadraticCurveTo(size / 4, y - size / 4, size / 2, y);
        context.quadraticCurveTo(size * 0.75, y + size / 4, size, y);
        context.stroke();
      }
    } else if (symbol.material === 'grass' || symbol.material === 'forest') {
      context.lineWidth = 2;
      for (const x of [size / 4, size / 2, size * 0.75]) {
        context.beginPath();
        context.moveTo(x, size);
        context.lineTo(x + size / 8, symbol.material === 'forest' ? size / 5 : size / 2);
        context.stroke();
      }
    } else {
      for (const [x, y, radius] of [[0.2, 0.25, 2], [0.65, 0.35, 1.5], [0.45, 0.8, 2]] as const) {
        context.beginPath();
        context.arc(size * x, size * y, radius, 0, Math.PI * 2);
        context.fill();
      }
    }
  }
}
